// New tagged slice for the migrated `_features/subscriptions` feature.
//
// The legacy `_features/subscriptions/api/subscriptionApiSlice.js` (still
// used by CustomerDetails, SelfServeDetails, and
// SubscriptionDataManagementTable - none in scope for this migration, see
// docs/known-issues.md) injects
// endpoints into the same shared apiSlice with no `overrideExisting` and no
// cache tags at all. Rather than fight over the same endpoint names (the
// same collision `_features/proposals/api/proposalApi.js` hit and resolved
// by renaming), this file uses its own names and adds proper
// providesTags/invalidatesTags so list/detail views actually revalidate on
// mutation.
//
// One real bug fixed while rewriting (not present in the legacy file, not
// carried forward): the legacy slice's injected object was named
// `integrationApiSlice` (a copy-paste leftover with zero connection to the
// Integrations feature).
import { apiRoutes } from "../../../_config/apiRoutes";
import { apiSlice } from "../../../_api/apiSlice";

export const subscriptionApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Company ids with a Trialing subscription (Companies "Trials" tab).
    getTrialCompanyIds: builder.query({
      query: () => ({
        url: apiRoutes.subscription.trialCompanyIds.path,
        method: apiRoutes.subscription.trialCompanyIds.reqType,
      }),
      providesTags: ["Subscriptions"],
    }),
    // Per-email Active/Trialing summary for self-serve signups (Self-serve users page).
    getSelfServeSubscriptionSummary: builder.query({
      query: () => ({
        url: apiRoutes.subscription.selfServeSummary.path,
        method: apiRoutes.subscription.selfServeSummary.reqType,
      }),
      providesTags: ["Subscriptions"],
    }),
    listSubscriptions: builder.query({
      query: (params) => ({
        url: apiRoutes.subscription.get.path,
        method: apiRoutes.subscription.get.reqType,
        params,
      }),
      providesTags: ["Subscriptions"],
    }),
    getSubscriptionById: builder.query({
      query: ({ id }) => ({
        url: `${apiRoutes.subscription.get.path}${id}`,
        method: apiRoutes.subscription.get.reqType,
      }),
      providesTags: (result, error, { id }) => [{ type: "Subscription", id }],
    }),
    getSubscriptionsByCompany: builder.query({
      query: ({ companyId }) => ({
        url: `${apiRoutes.subscription.get.path}company/${companyId}`,
        method: apiRoutes.subscription.get.reqType,
      }),
      providesTags: (result, error, { companyId }) => [
        { type: "CompanySubscriptions", id: companyId },
      ],
    }),
    submitSubscription: builder.mutation({
      query: (data) => ({
        url: apiRoutes.subscription.create.path,
        method: apiRoutes.subscription.create.reqType,
        body: data,
      }),
      invalidatesTags: ["Subscriptions", "RevenueCmrr"],
    }),
    updateSubscription: builder.mutation({
      query: (data) => ({
        url: apiRoutes.subscription.edit.path,
        method: apiRoutes.subscription.edit.reqType,
        body: data,
      }),
      invalidatesTags: (result, error, data) => [
        "Subscriptions",
        "RevenueCmrr",
        { type: "Subscription", id: data?._id },
      ],
    }),
    updateSubscriptionStatus: builder.mutation({
      query: (data) => ({
        url: `${apiRoutes.subscription.edit.path}changeStatus`,
        method: apiRoutes.subscription.edit.reqType,
        body: data,
      }),
      invalidatesTags: (result, error, data) => [
        "Subscriptions",
        "RevenueCmrr",
        { type: "Subscription", id: data?.id },
      ],
    }),
    deleteSubscription: builder.mutation({
      query: (data) => ({
        url: apiRoutes.subscription.delete.path,
        method: apiRoutes.subscription.delete.reqType,
        body: data,
      }),
      invalidatesTags: ["Subscriptions", "RevenueCmrr"],
    }),
    // Trial card-on-file capture (utils/subscription/subscriptionPaymentMethod.js)
    // and lapsed-trial reactivation (utils/subscription/reactivateSubscription.js)
    // in simplifyserver. No cache tags needed on the setup-intent/create-intent
    // pair - they don't change any data the UI reads elsewhere, only the
    // *confirm* steps do.
    createSubscriptionSetupIntent: builder.mutation({
      query: ({ id, email }) => ({
        url: `${apiRoutes.subscription.edit.path}${id}/payment-method/setup-intent`,
        method: "POST",
        body: { email },
      }),
    }),
    confirmSubscriptionPaymentMethod: builder.mutation({
      query: ({ id, setupIntentId }) => ({
        url: `${apiRoutes.subscription.edit.path}${id}/payment-method/confirm`,
        method: "POST",
        body: { setupIntentId },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "Subscription", id }],
    }),
    createReactivationIntent: builder.mutation({
      query: ({ id }) => ({
        url: `${apiRoutes.subscription.edit.path}${id}/reactivate/payment-intent`,
        method: "POST",
      }),
    }),
    confirmReactivation: builder.mutation({
      query: ({ id, paymentIntentId }) => ({
        url: `${apiRoutes.subscription.edit.path}${id}/reactivate/confirm`,
        method: "POST",
        body: { paymentIntentId },
      }),
      invalidatesTags: (result, error, { id }) => [
        "Subscriptions",
        "RevenueCmrr",
        { type: "Subscription", id },
      ],
    }),
  }),
});

export const {
  useListSubscriptionsQuery,
  useGetTrialCompanyIdsQuery,
  useGetSelfServeSubscriptionSummaryQuery,
  useGetSubscriptionByIdQuery,
  useGetSubscriptionsByCompanyQuery,
  useSubmitSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useUpdateSubscriptionStatusMutation,
  useDeleteSubscriptionMutation,
  useCreateSubscriptionSetupIntentMutation,
  useConfirmSubscriptionPaymentMethodMutation,
  useCreateReactivationIntentMutation,
  useConfirmReactivationMutation,
} = subscriptionApi;
